// src/utils/subdomain.js
//
// {username}.mintlink.in — a creator's website on its own address.
//
// HOW IT WORKS
// ────────────
// One deployment, one bundle. `*.mintlink.in` points at the same Vercel project
// as the apex, so ayush.mintlink.in serves the identical index.html; this file
// reads the Host the browser used and the router renders that creator's
// storefront at `/` instead of the marketing home page.
//
// No per-creator DNS, no provisioning step, no second deploy. Adding a creator
// costs nothing because nothing is added.
//
// WHAT HAS TO BE TRUE ON THE HOSTING SIDE (see STOREFRONT_SUBDOMAINS.md)
//   1. `*.mintlink.in` added as a domain on the Vercel project.
//   2. A wildcard TLS certificate, which Vercel will only issue when the
//      domain's nameservers are Vercel's, or via a DNS-01 CNAME they specify.
//      This is the only genuinely blocking step and it is a DNS change, not
//      code.
//   3. api/og.js reading the Host header, so a crawler on a subdomain gets the
//      creator's meta tags instead of the marketing page's.
//
// WHY A RESERVED LIST EXISTS
// ──────────────────────────
// `www`, `api`, `admin` and friends are subdomains we either use or will want.
// A creator who registered the username "admin" must not be handed
// admin.mintlink.in by this file. The list is deliberately generous: a name
// wrongly treated as reserved costs one creator a vanity URL, while a name
// wrongly treated as a username could hand somebody a company address.

/** Hosts that are never a creator, whatever the username table says. */
export const RESERVED_SUBDOMAINS = new Set([
  'www', 'api', 'app', 'admin', 'auth', 'cdn', 'static', 'assets', 'img', 'images',
  'mail', 'email', 'smtp', 'imap', 'mx', 'ns1', 'ns2', 'dns',
  'blog', 'help', 'support', 'docs', 'status', 'dev', 'staging', 'test', 'preview',
  'go', 'link', 'links', 'pay', 'payments', 'checkout', 'shop', 'store',
  'dashboard', 'account', 'my', 'me', 'about', 'careers', 'press', 'legal',
  'mintlink', 'plus', 'studio', 'creators', 'marketplace', 'explore',
]);

/** The apex the product runs on. Anything else — a preview deploy, localhost,
 *  a custom domain — is not a creator subdomain and must not be treated as one. */
export const ROOT_DOMAIN = 'mintlink.in';

/**
 * The creator whose site this hostname is for, or null.
 *
 * Deliberately strict. Every unexpected shape returns null, which means "render
 * MintLink normally" — the safe direction. Returning a wrong username here
 * would serve one creator's shop on another creator's address.
 */
export const storefrontHostFor = (hostname) => {
  const host = String(hostname || '').toLowerCase().trim().replace(/\.$/, '');
  if (!host) return null;

  // Strip a port, which shows up in local and preview environments.
  const bare = host.split(':')[0];

  if (!bare.endsWith(`.${ROOT_DOMAIN}`)) return null;

  const label = bare.slice(0, -(ROOT_DOMAIN.length + 1));

  // Only ONE level deep. `a.b.mintlink.in` is not a creator — it is either a
  // mistake or somebody probing, and either way it is not a username.
  if (!label || label.includes('.')) return null;

  if (RESERVED_SUBDOMAINS.has(label)) return null;

  // Exactly the rule the backend enforces on usernames
  // (app/routes/creators.py: ^[a-z0-9_]{3,30}$). Anything else cannot be a
  // creator, so there is no point asking the API about it.
  //
  // NOTE ON UNDERSCORES: usernames allow them, hostnames per RFC 1123 do not.
  // A creator called `a_b` may find that some resolvers and TLS clients refuse
  // a_b.mintlink.in entirely. That is a real limitation of the subdomain form,
  // not something this file can fix — they keep /o/a_b, which always works.
  // Parsed here anyway: if such a request DOES arrive, serving that creator is
  // the correct answer.
  if (!/^[a-z0-9_]{3,30}$/.test(label)) return null;

  return label;
};

/** The current window's creator subdomain, or null. Safe during SSR/tests. */
export const currentStorefrontHost = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  return storefrontHostFor(window.location.hostname);
};

// ── Custom domains ───────────────────────────────────────────────────────────
//
// A creator's own domain (example.com) cannot be parsed into a username the way
// a subdomain can — the hostname carries no creator in it, so the only way to
// know whose site this is, is to ask. That makes this function's job narrow and
// negative: decide whether a host is worth ASKING about, and refuse everything
// we can already account for.
//
// Getting this wrong in the permissive direction costs a pointless API call on
// every page of the marketing site; getting it wrong in the strict direction
// means a paying creator's domain silently renders MintLink's homepage. So the
// list below is exact rather than clever.

/** Hosts that are the product itself, a developer's machine, or a preview
 *  build — never a creator's own domain. */
const isOwnOrDevHost = (bare) => {
  if (bare === ROOT_DOMAIN || bare.endsWith(`.${ROOT_DOMAIN}`)) return true;
  if (bare === 'localhost' || bare.endsWith('.localhost') || bare.endsWith('.local')) return true;
  // Vercel preview and production deployment URLs.
  if (bare.endsWith('.vercel.app')) return true;
  // A bare IP is somebody hitting an origin directly, not a domain anyone
  // pointed at us on purpose.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(bare)) return true;
  if (bare.includes(':')) return true; // IPv6 literal
  return false;
};

/**
 * Is this hostname possibly a creator's connected domain?
 *
 * True means "ask the API whose it is" — never "it definitely is one". The
 * answer still comes from the server, which is the only thing that knows.
 */
export const isCustomDomainHost = (hostname) => {
  const host = String(hostname || '').toLowerCase().trim().replace(/\.$/, '');
  if (!host) return false;
  const bare = host.split(':')[0];
  if (!bare || isOwnOrDevHost(bare)) return false;
  // Must look like a real domain: at least one dot, and a plausible TLD.
  return /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(bare);
};

/** The current window's host if it might be a creator's own domain. */
export const currentCustomDomain = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  const bare = String(window.location.hostname || '').toLowerCase();
  return isCustomDomainHost(bare) ? bare.split(':')[0] : null;
};

/**
 * Where a creator's site actually lives.
 *
 * One function so the editor, the dashboard, the share sheet and the reminder
 * emails cannot disagree about a creator's own address. Flip PREFER_SUBDOMAIN
 * on the day the wildcard certificate is live, and every one of those surfaces
 * starts saying the same new thing at once.
 */
export const PREFER_SUBDOMAIN = false;

export const storefrontUrl = (username) => {
  const u = String(username || '').toLowerCase().trim();
  if (!u) return '';
  return PREFER_SUBDOMAIN
    ? `https://${u}.${ROOT_DOMAIN}`
    : `https://${ROOT_DOMAIN}/o/${u}`;
};

/** Both addresses, for the editor to show while the subdomain is rolling out. */
export const storefrontUrls = (username) => {
  const u = String(username || '').toLowerCase().trim();
  if (!u) return { path: '', subdomain: '' };
  return {
    path: `https://${ROOT_DOMAIN}/o/${u}`,
    subdomain: `https://${u}.${ROOT_DOMAIN}`,
  };
};
